import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import Seo from '../components/Seo';
import ScrollReveal from '../components/ScrollReveal';
import { useLanguage } from '../contexts/LanguageContext';
import { topicPages, topicSlugs, topicImages, TopicSlug } from '../data/topicPages';
import { servicePages } from '../data/servicePages';
import { COMPANY_WHATSAPP, consultationWhatsAppUrl } from '../data/whatsapp';
import './CountryPage.css';

interface TopicPageProps {
  slug: TopicSlug;
}

const WHATSAPP_URL = consultationWhatsAppUrl();

const CHINA_BOOKING_URL =
  `https://wa.me/${COMPANY_WHATSAPP}?text=` +
  encodeURIComponent(
    [
      'مرحباً شركة الشراع،',
      'أريد حجز مكان للشحن من الصين. النوع: كلي FCL أو جزئي LCL. المدينة في الصين: . نوع البضاعة: . الوزن أو CBM: .',
      'أُدرك أن الاستشارات الجمركية ودراسة الأسعار خدمة مدفوعة وليست مجانية.',
    ].join('\n')
  );

const TopicPage: React.FC<TopicPageProps> = ({ slug }) => {
  const { language } = useLanguage();
  const content = topicPages[slug]?.[language];
  const actionsRef = useRef<HTMLDivElement>(null);
  const [shimmer, setShimmer] = useState(false);

  useEffect(() => {
    const node = actionsRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShimmer(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [slug]);

  if (!content) {
    return <Navigate to="/" replace />;
  }

  const path = `/${slug}`;
  const whatsappUrl = slug === 'china-fcl-lcl' ? CHINA_BOOKING_URL : WHATSAPP_URL;
  const related = topicSlugs.filter((item) => item !== slug);
  const image = topicImages[slug];
  const labels = {
    ar: {
      home: 'الشراع',
      related: 'مواضيع ذات صلة',
      services: 'خدماتنا',
      contact: 'نموذج التواصل',
    },
    en: {
      home: 'Al Shiraa',
      related: 'Related topics',
      services: 'Services',
      contact: 'Contact form',
    },
    de: {
      home: 'Al Shiraa',
      related: 'Verwandte Themen',
      services: 'Leistungen',
      contact: 'Kontaktformular',
    },
  }[language];

  return (
    <article className="country-page">
      <Seo
        title={content.title}
        description={content.description}
        path={path}
        imageUrl={image?.src}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Service',
              name: content.h1,
              serviceType: content.name,
              provider: {
                '@type': 'LocalBusiness',
                name: 'شركة الشراع للتخليص الجمركي',
                url: 'https://alshiraa-customs.com/',
                telephone: '+963933612861',
                address: {
                  '@type': 'PostalAddress',
                  addressLocality: 'Latakia',
                  addressCountry: 'SY',
                },
              },
              url: `https://alshiraa-customs.com${path}`,
              description: content.description,
              areaServed: 'Syria',
              image: image?.src?.startsWith('http')
                ? image.src
                : `https://alshiraa-customs.com${image?.src || '/og-image.png'}`,
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: labels.home,
                  item: 'https://alshiraa-customs.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: content.name,
                  item: `https://alshiraa-customs.com${path}`,
                },
              ],
            },
            {
              '@type': 'FAQPage',
              mainEntity: content.faqs.map((item) => ({
                '@type': 'Question',
                name: item.q,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: item.a,
                },
              })),
            },
          ],
        }}
      />
      <div className="container country-page-inner">
        <div className="country-actions country-actions-top" ref={actionsRef}>
          <a
            className={`btn btn-primary btn-shimmer ${shimmer ? 'is-shimmering' : ''}`}
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {content.cta}
          </a>
          <Link className={`btn btn-secondary btn-shimmer ${shimmer ? 'is-shimmering' : ''}`} to="/#quick-quote">
            {content.quoteCta}
          </Link>
        </div>
        <p className="country-breadcrumb">
          <Link to="/">{labels.home}</Link>
          <span> / </span>
          <span>{content.name}</span>
        </p>
        <h1 className="country-heading">{content.h1}</h1>
        {image ? (
          <figure className="country-hero-figure">
            <img
              className="country-hero-image"
              src={image.src}
              alt={image.alt[language]}
              width={1200}
              height={675}
              loading="eager"
            />
          </figure>
        ) : null}
        <p className="country-intro">{content.intro}</p>
        {content.paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        <h2>{content.benefitsTitle}</h2>
        <ScrollReveal variant="fade-up" asStagger>
          <ul className="country-crossings">
            {content.benefits.map((item, index) => (
              <li key={item} className="stagger-item" style={{ transitionDelay: `${index * 70}ms` }}>
                <span className="country-list-dot" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </ScrollReveal>
        <h2>{content.faqTitle}</h2>
        <ScrollReveal variant="fade-up" asStagger>
          <ul className="country-crossings">
            {content.faqs.map((item, index) => (
              <li key={item.q} className="stagger-item" style={{ transitionDelay: `${index * 55}ms` }}>
                <span className="country-list-dot" aria-hidden="true" />
                <span>
                  <strong>{item.q}</strong>
                  <br />
                  {item.a}
                </span>
              </li>
            ))}
          </ul>
        </ScrollReveal>
        <div className="country-actions">
          <a
            className={`btn btn-primary btn-shimmer ${shimmer ? 'is-shimmering' : ''}`}
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {content.cta}
          </a>
          <Link className="btn btn-secondary" to="/#quick-quote">
            {content.quoteCta}
          </Link>
          <Link className="btn btn-secondary" to="/#contact">
            {labels.contact}
          </Link>
          <Link className="btn btn-secondary" to="/services/customs-clearance">
            {labels.services}: {servicePages['customs-clearance'][language].name}
          </Link>
        </div>
        <h2>{labels.related}</h2>
        <ScrollReveal variant="fade-scale" asStagger>
          <div className="country-related">
            {related.map((item, index) => (
              <Link
                key={item}
                className="country-related-card interactive-lift stagger-item"
                to={`/${item}`}
                style={{ transitionDelay: `${index * 55}ms` }}
              >
                {topicPages[item][language].name}
              </Link>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </article>
  );
};

export default TopicPage;
